<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import type { CustomerSubscription, StripeProductMap } from '@functions/types/subscription';
import { captureException } from '@sentry/vue';
import { httpsCallable } from 'firebase/functions';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getSubscriptionPlans as _getSubscriptionPlans, getSubscription } from '@/api';
import { useDebugUser } from '@/composables/useDebugUser';
import { functions } from '@/firebase';

definePage({
  name: 'pricing',
});

const router = useRouter();
const toast = useToast();
const user = useDebugUser();
const loading = ref(false);
const loadingSubscriptionPlans = ref(false);
const subscription = ref<CustomerSubscription | null>(null);
const subscriptionPlans = ref<StripeProductMap | null>(null);

async function loadSubscription() {
  if (!user.value) return;

  try {
    const data = await getSubscription();
    subscription.value = data;
  }
  catch (error) {
    console.error('Error loading subscription:', error);
    captureException(error);
  }
}

async function getSubscriptionPlans() {
  loadingSubscriptionPlans.value = true;
  const data = await _getSubscriptionPlans();
  subscriptionPlans.value = data.plans;
  loadingSubscriptionPlans.value = false;
}
async function handleSubscribe(priceId: string) {
  if (!user.value) {
    router.push('/login');
    return;
  }

  try {
    loading.value = true;
    const createCheckoutSession = httpsCallable<
      { priceId: string; successUrl: string; cancelUrl: string },
      { url: string }
    >(functions, 'createSubscriptionCheckout');

    const { data } = await createCheckoutSession({
      priceId,
      successUrl: `${window.location.origin}/account?subscription=success`,
      cancelUrl: `${window.location.origin}/pricing?subscription=canceled`,
    });

    // Redirect to Stripe Checkout
    window.location.href = data.url;
  }
  catch (error) {
    console.error('Error creating checkout session:', error);
    captureException(error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not initiate checkout. Please try again.',
      life: 3000,
    });
  }
  finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadSubscription();
  getSubscriptionPlans();
});
</script>

<template>
  <div class="pricing-container p-6 max-w-7xl mx-auto">
    <div class="text-center mb-12">
      <h1 class="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
        Choose Your Plan
      </h1>

      <div v-if="loadingSubscriptionPlans || loading" class="flex justify-center items-center">
        <span class="text-xl text-gray-600 dark:text-white mb-4">Getting subscription plans...</span>
        <ProgressSpinner />
      </div>
      <div v-else>
        <p class="text-xl text-gray-600 dark:text-white mb-4">
          Select the perfect plan for your golf game improvement journey
        </p>

        <!-- Trial Status Message -->
        <div v-if="subscription?.trial" class="max-w-2xl mx-auto mb-8">
          <Message severity="info" :closable="false">
            <template v-if="subscription.trial.daysRemaining > 0">
              <span class="font-medium">You're currently on a free trial!</span>
              <br>{{ subscription.trial.daysRemaining }} days remaining - Upgrade now to continue accessing all
              features after your trial ends.
            </template>
            <template v-else>
              Your trial has ended - Subscribe now to continue accessing all features.
            </template>
          </Message>
        </div>
      </div>

      <div id="pricing-plans" class="flex flex-col md:flex-row gap-8 justify-center">
        <div
          v-for="plan in subscriptionPlans"
          :key="plan.name"
          class="bg-white rounded-lg shadow-lg overflow-hidden w-96 h-100"
          :class="[plan.name === 'Pro Plan (Annual)' ? 'border-2 border-green-500' : 'border border-gray-200']"
        >
          <div class="p-6 h-full flex flex-col">
            <h2 class="text-2xl font-bold mb-2">
              {{ plan.name }}
            </h2>
            <div class="flex items-baseline mb-4">
              <span class="text-4xl font-bold">${{ plan.price }}</span>
              <span v-if="plan.interval" class="text-gray-500 ml-2">/{{ plan.interval }}</span>
            </div>
            <ul class="mb-6 space-y-3">
              <li v-for="feature in plan.features" :key="feature" class="flex items-center text-gray-600">
                <svg
                  class="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {{ feature }}
              </li>
            </ul>
            <Button
              :loading="loading"
              class="w-full mt-auto"
              :class="plan.name === 'Pro Plan (Annual)' ? 'p-button-primary' : 'p-button-outlined text-black'"
              @click="handleSubscribe(plan.id)"
            >
              {{ subscription?.trial ? 'Upgrade Now' : 'Get Started' }}
            </Button>
          </div>
        </div>
      </div>

      <div class="mt-12 text-center">
        <p class="text-gray-600 dark:text-white">
          Questions about our plans?
          <a href="/support" class="text-green-600 hover:underline">Contact our support team</a>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pricing-container {
  min-height: calc(100vh - 64px);
}
#pricing-plans {
  --p-button-outlined-primary-color: #000;
}
</style>
